import { apiFetch } from './api.js';

// AI spend is metered in USD server-side — that is what Anthropic bills and what
// the cap enforces — but fractions of a cent are unreadable: "$0.0612" for one
// report summary tells an owner nothing. Display whole "credits" instead. 10,000
// credits = $1, so 1 credit = $0.0001 and every real figure lands on a readable
// integer: an SMS draft 8, a vehicle description 15, a debtor summary 23, a
// report summary ~610 (Opus), and the default monthly allowance 10,000.
//
// Presentation only. The endpoints, the budget column, the cap and the alert
// emails all stay in USD; if credits ever become something users buy, this
// factor moves server-side. Lives here rather than in a screen because both
// Settings (spend to date) and Reports (cost disclosure) render it, and the two
// must never disagree about what a credit is worth.
export const CREDITS_PER_USD = 10000;
export const toCredits = (usd) => Math.round((Number(usd) || 0) * CREDITS_PER_USD);

/**
 * Shared helper: runs apiFetch and throws a typed error on non-OK responses.
 * The thrown object always has { status, aiDisabled } so callers can branch
 * on rate-limit (429) vs kill-switch (503 + aiDisabled) vs generic failures.
 */
async function aiPost(path, body) {
    const res = await apiFetch(path, { body });
    if (res.ok) {
        return res.json();
    }
    // Build a typed error with the fields callers care about.
    let aiDisabled = false;
    let notImplemented = false;
    let budgetExceeded = false;
    let serverMessage = '';
    try {
        const json = await res.json();
        aiDisabled = Boolean(json.aiDisabled);
        notImplemented = Boolean(json.notImplemented);
        budgetExceeded = Boolean(json.budgetExceeded);
        serverMessage = json.message || '';
    } catch {
        // ignore parse failures — status alone is enough
    }
    const err = new Error(
        aiDisabled
            ? 'AI features are currently disabled.'
            : notImplemented
            ? 'This AI feature is not yet available.'
            // Keep the server's budget wording verbatim — it names the reset timing.
            : budgetExceeded && serverMessage
            ? serverMessage
            : `AI request failed (HTTP ${res.status})`
    );
    err.status = res.status;
    err.aiDisabled = aiDisabled;
    err.notImplemented = notImplemented;
    err.budgetExceeded = budgetExceeded;
    throw err;
}

/**
 * GET /ai/usage — month-to-date AI spend for the caller's dealership.
 * Gated server-side on settings.edit. Returns
 * { month, spentUsd, budgetUsd, pctUsed, calls, byFeature: [{feature, calls, usd}] }.
 * Throws on failure so the Settings card can show an inline error.
 */
export async function fetchAiUsage() {
    const res = await apiFetch('/ai/usage', { method: 'GET' });
    if (!res.ok) {
        const err = new Error(`Could not load AI usage (HTTP ${res.status})`);
        err.status = res.status;
        throw err;
    }
    return res.json();
}

/**
 * GET /ai/report-quote — what one AI report summary will cost, and what is left
 * of this month's allowance. Backs the cost disclosure shown when a user opts
 * into the paid summary, so unlike fetchAiUsage() it needs only reports.view.
 * Returns { estimateUsd, worstCaseUsd, basedOnHistory, budgetUsd, spentUsd,
 * remainingUsd, sufficient }.
 */
export async function fetchReportQuote() {
    const res = await apiFetch('/ai/report-quote', { method: 'GET' });
    if (!res.ok) {
        const err = new Error(`Could not load AI cost estimate (HTTP ${res.status})`);
        err.status = res.status;
        throw err;
    }
    return res.json();
}

/**
 * GET /ai/status — lightweight probe called once per login.
 * Returns { enabled: boolean }. Does NOT throw on 401/network failure:
 * callers treat any error as enabled=false.
 */
export async function fetchAiStatus() {
    const res = await apiFetch('/ai/status', { method: 'GET' });
    if (!res.ok) return { enabled: false };
    try {
        return await res.json(); // { enabled: boolean }
    } catch {
        return { enabled: false };
    }
}

/**
 * POST /ai/describe-vehicle — generate listing description for one vehicle.
 * @param {{ vehicleId: number|string, tone?: 'standard'|'premium' }} params
 * @returns {{ description: string, featuredDescription: string }}
 */
export async function describeVehicle({ vehicleId, tone }) {
    return aiPost('/ai/describe-vehicle', { vehicleId, tone });
}

/**
 * POST /ai/describe-batch — bulk description backfill (≤50 ids/request).
 * `tone` matches describeVehicle ('standard' | 'premium'); omitting it means
 * standard. The route used to hardcode the standard tone and accept no tone at
 * all, so bulk copy could not be written in the same voice as single-vehicle
 * copy — and bulk is what writes most of a dealership's public site.
 * @param {{ vehicleIds: (number|string)[], tone?: 'standard'|'premium' }} params
 * @returns {Object} per-id results
 */
export async function describeBatch({ vehicleIds, tone }) {
    return aiPost('/ai/describe-batch', { vehicleIds, tone });
}

/**
 * POST /ai/draft-reminder — draft an SMS payment reminder.
 * purpose: 'overdue'|'upcoming'|'final-demand'|'thank-you'|'payment-plan'
 * tone: 'friendly'|'neutral'|'firm'; extras ⊆ ['mention-penalty','offer-plan',
 * 'request-callback','single-segment']; hint: ≤120-char staff context note.
 * @returns {{ message: string }}
 */
export async function draftReminder({ customerId, dealAgreementNo, purpose, tone, extras, hint }) {
    return aiPost('/ai/draft-reminder', { customerId, dealAgreementNo, purpose, tone, extras, hint });
}

/**
 * POST /ai/draft-reminder (mode "template") — draft a reusable reminder
 * template with {{placeholder}} variables for a bulk send. No customer data
 * is sent; placeholders are the composer's own variable field ids. Tuning
 * params match draftReminder.
 * @returns {{ message: string }}
 */
export async function draftReminderTemplate({ purpose, tone, extras, hint, placeholders, recipientCount }) {
    return aiPost('/ai/draft-reminder', { mode: 'template', purpose, tone, extras, hint, placeholders, recipientCount });
}

/**
 * POST /ai/report-summary — AI narrative for a generated report.
 * @param {{ startDate: string, endDate: string }} params
 * @returns {{ headline: string, performance: string, pricingStock: string, creditRisk: string }}
 */
export async function fetchReportSummary({ startDate, endDate }) {
    return aiPost('/ai/report-summary', { startDate, endDate });
}

/**
 * POST /ai/debtor-summary — cached risk summary for a credit customer.
 * @param {{ customerId: number|string, force?: boolean }} params
 * @returns {{ synopsis: string, trend: 'improving'|'stable'|'deteriorating', suggestedAction: string, cached?: boolean }}
 */
export async function fetchDebtorSummary({ customerId, force }) {
    return aiPost('/ai/debtor-summary', { customerId, force });
}
